import React, { useState } from 'react';
import { color } from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { SortableContainer } from 'react-sortable-hoc';
import { FlexBox, Typography, Button, Box, Card } from '@kis-technologies/kis-ui';
import { selectors as configurationsSelectors } from 'store/configurations';
import { MediaResponse } from 'views/MediaCenterView/types';
import { SortableRow } from '../MediaRow';
import { TransitionTimeDrawer } from './TransitionTimeDrawer';
import { ImagePreview } from '../ImagePreview';
import { MediaText } from '../MediaText';
import { SlideshowImageListProps } from '../../../types';

export const SlideshowImageList: React.FC<SlideshowImageListProps> = (props: SlideshowImageListProps) => {
  const { id, slideShowImages, activeImage, previewUrl, isActive, mediaType, parentObj, configuration } = props;
  const [showSlideTime, setShowSlideTime] = useState(false);
  const loading = useSelector(configurationsSelectors.selectPending);
  const isAddImageDisabled = slideShowImages && slideShowImages.length >= 10;
  const hasImages = Boolean(slideShowImages && slideShowImages.length);
  const transitionTime = (parentObj && parentObj.transitionTime) || 5;
  const accentColor = activeImage ? color.PRIMARY : color.BLACK;

  const onMakeActive = () => {
    props.updateConfig({ type: mediaType });
  };

  const SortableList = SortableContainer(() => {
    return (
      <Box sx={{ maxHeight: '329px', overflowY: 'auto', borderLeft: { accentColor } }}>
        {activeImage &&
          slideShowImages.map((image, idx) => (
            <SortableRow
              index={idx}
              key={image.s3Name}
              label={image.name}
              selected={Boolean(activeImage && activeImage.s3Name === image.s3Name)}
              cursor={'grab'}
              onClick={() => {
                if (activeImage && activeImage.id !== image.id) {
                  props.setActiveImage(image);
                }
              }}
              onEditClick={() => props.onImageEditClick(image.s3Name, image.name, image.id)}
              onDeleteClick={() => props.onDelete(image)}
            />
          ))}
      </Box>
    );
  });

  const arrayMove = (arr: MediaResponse[], fromIndex: number, toIndex: number): MediaResponse[] => {
    const newArr = [...arr];
    const element = newArr[fromIndex];
    newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, element);

    return newArr;
  };

  const onSortEnd = (sortProps: { oldIndex: number; newIndex: number }) => {
    if (sortProps.oldIndex !== sortProps.newIndex) {
      const newOrder = arrayMove(slideShowImages, sortProps.oldIndex, sortProps.newIndex).map((item) => item.s3Name);
      props.updateConfig({ order: newOrder });
    }
  };

  return (
    <>
      {hasImages ? (
        <>
          <TransitionTimeDrawer
            configuration={configuration}
            isOpen={showSlideTime}
            transitionTime={transitionTime}
            mediaType={mediaType}
            parentObj={parentObj}
            configurationId={id}
            onClose={() => setShowSlideTime(false)}
          />
          <FlexBox fullWidth noWrap>
            <Box width={650}>
              <Card raised hasBorderRadius>
                <Card.Title
                  title={
                    <FlexBox direction="column" align="start" fullWidth>
                      <FlexBox display="row" fullWidth align="center" justify="between">
                        <Typography fontSize={18} fontWeight={600}>
                          Slideshow
                        </Typography>
                        {slideShowImages.length > 1 && (
                          <Button
                            customSize="x-small"
                            onClick={() => setShowSlideTime(true)}
                            isLoading={loading}
                            appearance="primary"
                            data-testid="add-image-button"
                          >
                            Set Slide Time
                          </Button>
                        )}
                        {!isAddImageDisabled && (
                          <Button
                            customSize="x-small"
                            appearance="primary"
                            onClick={props.onAddImageClick}
                            isLoading={loading}
                            data-testid="add-image-button"
                          >
                            Add Image
                          </Button>
                        )}
                        <FlexBox>
                          {isActive && !loading ? (
                            <Typography fontSize={18} fontWeight={600} color={color.PRIMARY}>
                              ACTIVE
                            </Typography>
                          ) : (
                            <Button
                              customSize="x-small"
                              appearance="primary"
                              onClick={onMakeActive}
                              isLoading={loading}
                            >
                              Make Active
                            </Button>
                          )}
                        </FlexBox>
                      </FlexBox>
                    </FlexBox>
                  }
                  gutter={5}
                />
                <Card.Content gutter={0}>
                  {slideShowImages.length > 1 ? (
                    <Box margin="2 4" fullWidth sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography fontSize={14}>Drag and drop an image to reorder your slideshow</Typography>
                    </Box>
                  ) : null}
                  <SortableList onSortEnd={onSortEnd} distance={1} />
                </Card.Content>
              </Card>
              {isAddImageDisabled && (
                <Box margin="4 0">
                  <Typography color={color.RED}>You can have up to 10 images in your slideshow.</Typography>
                </Box>
              )}
            </Box>
            {activeImage && (
              <ImagePreview
                previewUrl={previewUrl}
                activeImage={activeImage && activeImage.name}
                imageType={mediaType}
              />
            )}
          </FlexBox>
        </>
      ) : (
        <Box width={650}>
          <Card raised hasBorderRadius>
            <Card.Title
              title={
                <Typography fontSize={18} fontWeight={600}>
                  Slideshow
                </Typography>
              }
              gutter={5}
            />
            <Card.Content>
              <Box>
                <MediaText mediaType={mediaType} />
                <Box height={20} />
                <Button customSize="small" width={160} onClick={props.onAddImageClick} data-testid="add-image-button">
                  Add Image
                </Button>
              </Box>
            </Card.Content>
          </Card>
        </Box>
      )}
    </>
  );
};
